"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import { ChevronLeft, ChevronRight, Leaf, Shield, Truck, Sparkles, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useLanguage } from "@/lib/language-context"
import type { HeroSlide } from "@/lib/api"

interface HeroCarouselProps {
  slides?: HeroSlide[]
}

function getImageUrl(slide: HeroSlide): string {
  if (slide.image_url) return slide.image_url
  if (slide.image) return slide.image
  return `/placeholder.svg?height=800&width=1920&query=${encodeURIComponent(slide.title + " natural herbal medicine")}`
}

export function HeroCarousel({ slides = [] }: HeroCarouselProps) {
  const { t } = useLanguage()
  const safeSlides = Array.isArray(slides) ? slides : []
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    setIsLoaded(true)
  }, [])

  const goToNext = useCallback(() => {
    if (isAnimating || safeSlides.length === 0) return
    setIsAnimating(true)
    setCurrentIndex((prev) => (prev + 1) % safeSlides.length)
    setTimeout(() => setIsAnimating(false), 800)
  }, [isAnimating, safeSlides.length])

  const goToPrevious = useCallback(() => {
    if (isAnimating || safeSlides.length === 0) return
    setIsAnimating(true)
    setCurrentIndex((prev) => (prev - 1 + safeSlides.length) % safeSlides.length)
    setTimeout(() => setIsAnimating(false), 800)
  }, [isAnimating, safeSlides.length])

  useEffect(() => {
    if (safeSlides.length === 0) return
    const timer = setInterval(goToNext, 7000)
    return () => clearInterval(timer)
  }, [goToNext, safeSlides.length])

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setMousePosition({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    })
  }

  if (safeSlides.length === 0) {
    return (
      <div className="relative w-full min-h-[650px] lg:min-h-[750px] bg-gradient-to-br from-primary/5 via-background to-accent/10 flex items-center justify-center overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-float-slow" />
          <div
            className="absolute bottom-20 right-10 w-96 h-96 bg-accent/15 rounded-full blur-3xl animate-float-slow"
            style={{ animationDelay: "2s" }}
          />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary/5 rounded-full blur-3xl animate-pulse" />

          {/* Floating leaves */}
          <Leaf
            className="absolute top-24 right-1/4 w-10 h-10 text-primary/20 animate-float"
            style={{ animationDelay: "0s" }}
          />
          <Leaf
            className="absolute bottom-32 left-1/4 w-8 h-8 text-primary/15 animate-float"
            style={{ animationDelay: "1s" }}
          />
          <Leaf
            className="absolute top-1/3 left-16 w-12 h-12 text-primary/20 animate-float"
            style={{ animationDelay: "2s" }}
          />
          <Sparkles className="absolute top-40 right-20 w-6 h-6 text-herb-gold/40 animate-pulse" />
        </div>

        <div
          className={cn(
            "relative text-center px-4 max-w-4xl mx-auto transition-all duration-1000",
            isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
          )}
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-5 py-2.5 rounded-full text-sm font-semibold mb-8 animate-bounce-in">
            <Leaf className="w-4 h-4" />
            {t("home", "hero_badge")}
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-balance leading-[1.1]">
            <span className="block">{t("home", "hero_title_1")}</span>
            <span className="gradient-text block mt-2">{t("home", "hero_title_2")}</span>
          </h1>

          <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto text-pretty leading-relaxed">
            {t("home", "hero_description")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              size="lg"
              className="text-lg px-8 py-6 shadow-xl shadow-primary/20 hover:shadow-2xl hover:shadow-primary/30 transition-all group btn-glow"
            >
              {t("home", "hero_cta_primary")}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8 py-6 bg-transparent hover:bg-primary/5">
              {t("home", "hero_cta_secondary")}
            </Button>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-8 pt-8 border-t border-border/50">
            {[
              { icon: Shield, text: t("home", "trust_quality") },
              { icon: Truck, text: t("home", "trust_shipping") },
              { icon: Leaf, text: t("home", "trust_natural") },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors cursor-default group"
              >
                <item.icon className="w-5 h-5 text-primary group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative w-full min-h-[650px] lg:min-h-[750px] overflow-hidden" onMouseMove={handleMouseMove}>
      {/* Slides */}
      {safeSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={cn(
            "absolute inset-0 transition-all duration-1000 ease-out",
            index === currentIndex ? "opacity-100 scale-100 z-10" : "opacity-0 scale-105 z-0",
          )}
        >
          {/* Background with parallax */}
          <div
            className="absolute inset-0 transition-transform duration-200 ease-out"
            style={{
              transform:
                index === currentIndex
                  ? `translate(${(mousePosition.x - 0.5) * -30}px, ${(mousePosition.y - 0.5) * -30}px) scale(1.15)`
                  : "scale(1.15)",
            }}
          >
            <img
              src={getImageUrl(slide) || "/placeholder.svg"}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-transparent to-transparent" />

          {/* Content */}
          <div className="relative h-full container mx-auto px-4 flex items-center">
            <div className="max-w-2xl">
              {/* Badge */}
              <div
                className={cn(
                  "inline-flex items-center gap-2 bg-primary/15 backdrop-blur-sm text-primary px-5 py-2.5 rounded-full text-sm font-semibold mb-6",
                  "transition-all duration-700 delay-100",
                  index === currentIndex ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-6",
                )}
              >
                <Leaf className="w-4 h-4 animate-pulse" />
                {t("home", "hero_badge")}
              </div>

              {/* Title */}
              <h1
                className={cn(
                  "text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 text-balance leading-[1.1]",
                  "transition-all duration-700 delay-200",
                  index === currentIndex ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10",
                )}
              >
                {slide.title}
              </h1>

              {/* Subtitle */}
              <p
                className={cn(
                  "text-lg md:text-xl text-muted-foreground mb-8 text-pretty max-w-xl leading-relaxed",
                  "transition-all duration-700 delay-300",
                  index === currentIndex ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10",
                )}
              >
                {slide.subtitle}
              </p>

              {/* Buttons */}
              <div
                className={cn(
                  "flex flex-col sm:flex-row gap-4",
                  "transition-all duration-700 delay-400",
                  index === currentIndex ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10",
                )}
              >
                <Button
                  size="lg"
                  className="text-lg px-8 py-6 shadow-xl shadow-primary/25 hover:shadow-2xl hover:shadow-primary/35 transition-all group btn-glow"
                  onClick={() => {
                    const element = document.querySelector(slide.button_link || "#products")
                    element?.scrollIntoView({ behavior: "smooth" })
                  }}
                >
                  {slide.button_text || t("home", "hero_cta_primary")}
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 py-6 backdrop-blur-sm bg-background/20 hover:bg-background/40 border-foreground/20"
                >
                  {t("home", "hero_cta_secondary")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Navigation arrows */}
      {safeSlides.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-14 h-14 bg-background/80 hover:bg-background backdrop-blur-sm shadow-xl border border-border/50 hover:scale-110 transition-all z-20"
            onClick={goToPrevious}
            disabled={isAnimating}
          >
            <ChevronLeft className="h-7 w-7" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 w-14 h-14 bg-background/80 hover:bg-background backdrop-blur-sm shadow-xl border border-border/50 hover:scale-110 transition-all z-20"
            onClick={goToNext}
            disabled={isAnimating}
          >
            <ChevronRight className="h-7 w-7" />
          </Button>
        </>
      )}

      {/* Progress indicators */}
      {safeSlides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-20">
          {safeSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                if (!isAnimating && index !== currentIndex) {
                  setIsAnimating(true)
                  setCurrentIndex(index)
                  setTimeout(() => setIsAnimating(false), 800)
                }
              }}
              className="group relative h-3 transition-all duration-500"
            >
              <span
                className={cn(
                  "block h-full rounded-full transition-all duration-500",
                  index === currentIndex
                    ? "w-14 bg-primary shadow-lg shadow-primary/30"
                    : "w-3 bg-foreground/30 group-hover:bg-foreground/50 group-hover:w-5",
                )}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
